import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { RevolveFeatureDefinitionModel } from '../../../definition/revolve/RevolveFeatureDefinition';
import { BossRevolveFeaturePrototype } from '../../../prototype/revolve/BossRevolveFeaturePrototype';
import { CutRevolveFeaturePrototype } from '../../../prototype/revolve/CutRevolveFeaturePrototype';
import type { RevolveFeaturePrototype } from '../../../prototype/revolve/RevolveFeaturePrototype';
import { resolveSketchProfileSourceSketchId } from '../../../../utils/feature/resolveSketchProfileSourceSketchId';
import { FeatureCommitExecutionHandler, type FeatureCommitExecutionInput, type FeatureCommitExecutionResult } from '../FeatureCommitExecutionHandler';
import { RevolveCommitExecution } from './RevolveCommitExecution';
import type { FeatureExecutionRuntime } from '../../FeatureExecutionRuntime';

/**
 * 旋转特征提交执行处理器。
 */
export class RevolveCommitExecutionHandler extends FeatureCommitExecutionHandler {
    /** 旋转提交执行器。 */
    private readonly execution: RevolveCommitExecution;
    /** 特征执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;

    /**
     * @param execution 旋转提交执行器。
     */
    public constructor(execution: RevolveCommitExecution, runtime: FeatureExecutionRuntime) {
        super();
        this.execution = execution;
        this.runtime = runtime;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeatureCommitExecutionInput): boolean {
        return (input.featureKind === 'boss-revolve' || input.featureKind === 'cut-revolve')
            && input.definition instanceof RevolveFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当提交输入中的定义不是旋转特征定义时。
     */
    public async execute(input: FeatureCommitExecutionInput): Promise<FeatureCommitExecutionResult> {
        if (!(input.definition instanceof RevolveFeatureDefinitionModel)) {
            throw new Error('旋转提交处理器收到的 definition 类型不正确');
        }
        const featureId = input.featureId ?? `feature:${Date.now()}`;
        const PrototypeCtor = input.featureKind === 'cut-revolve'
            ? CutRevolveFeaturePrototype
            : BossRevolveFeaturePrototype;
        const sourceSketchId = resolveSketchProfileSourceSketchId({
            definitionSourceSketchId: input.definition.getSourceSketchId(),
            bindings: input.bindings,
        });
        const prototype = new PrototypeCtor({
            featureId,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId,
        });
        if (input.session) {
            const replaced = await this.execution.commitEdit(
                input.session as FeatureEditSession<RevolveFeatureDefinitionModel, RevolveFeaturePrototype>,
                prototype,
            );
            return {
                replaced,
                featureId,
                prototype,
                meshData: null,
            };
        }
        const committedObjectIds = await this.execution.commitCreate(prototype);
        const objectIds = Array.isArray(committedObjectIds)
            ? committedObjectIds
            : [committedObjectIds];
        const meshDataList = await Promise.all(
            objectIds.map((objectId) => this.runtime.generateMeshData(objectId)),
        );
        return {
            replaced: false,
            featureId,
            prototype,
            meshData: meshDataList[0] ?? null,
            objectId: objectIds[0],
            objectIds,
            meshDataList,
        };
    }
}
